import { analyticsEvent } from '@/utils/analyticsEvent';

import { EventTracker } from '@/utils';
import { cn } from '@/utils/styles';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
} from '@design-system/Sheet';
import { X } from 'lucide-react';
import { useEffect } from 'react';

import { useOrderTrackingViewModel } from '@/features/OrderTrackingViewModels';

import { OrderTrackingSkeleton } from './OrderTrackingSkeleton';
import { OrderTrackingTimeline } from './OrderTrackingTimeline';

interface OrderTrackingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const formatDate = (dateString: string | null): string | null => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

export const OrderTrackingModal = ({
  isOpen,
  onClose,
}: OrderTrackingModalProps) => {
  const { isLoading, stepGroups, hasCarrierTracking, orderInfo, hasOrder } =
    useOrderTrackingViewModel();

  useEffect(() => {
    if (isOpen) {
      EventTracker.trackEvent(
        'account',
        analyticsEvent.ORDER_TRACKING_DETAIL_VIEW,
      );
    }
  }, [isOpen]);

  if (!isOpen) return null;

  if (isLoading) {
    return <OrderTrackingSkeleton isOpen={isOpen} onClose={onClose} />;
  }

  if (!hasOrder) return null;

  const handleCarrierLinkClick = () => {
    EventTracker.trackEvent(
      'account',
      analyticsEvent.ORDER_TRACKING_CARRIER_LINK_TAP,
    );
  };

  return (
    <Sheet
      open={isOpen}
      onOpenChange={onClose}
      data-testid="orderTracking-modal"
    >
      <SheetPortal>
        <SheetOverlay data-testid="orderTracking-overlay" />
        <SheetContent
          side="right"
          className={cn(
            'pr-3 pl-6',
            'bg-background-secondary',
            'shadow-[0px_1px_0px_0px_#FFFFFF1A_inset,0px_4px_40px_0px_#44445E29]',
          )}
          variant="glass"
          hideCloseButton
          data-testid="orderTracking-content"
        >
          <SheetHeader
            className="mb-0 pt-6 pl-1 mt-3"
            data-testid="orderTracking-header"
          >
            <div>
              <div
                className="flex items-center justify-between"
                data-testid="orderTracking-headerTop"
              >
                <h2
                  className="typography-h1 text-primary"
                  data-testid="orderTracking-title"
                >
                  Order Tracking
                </h2>
                <button
                  onClick={onClose}
                  className="text-static-secondary hover:text-static-primary transition-colors p-2"
                  aria-label="Close tracking panel"
                  data-testid="orderTracking-closeButton"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {orderInfo && (
                <div
                  className="mt-6 flex flex-col gap-3 px-1"
                  data-testid="orderTracking-metadata"
                >
                  <p className="typography-caption1 font-light text-secondary">
                    Order Number: {orderInfo.shopify_order_number}
                  </p>
                  {orderInfo.order_date && (
                    <p className="typography-caption1 font-light text-secondary">
                      Order Date: {formatDate(orderInfo.order_date)}
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="mt-8 mb-4 flex flex-col px-1">
              {hasCarrierTracking ? (
                <a
                  href={orderInfo?.tracking_url ?? '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleCarrierLinkClick}
                  className="typography-caption1 font-light text-blue-500 underline"
                >
                  Track with {orderInfo?.carrier}
                </a>
              ) : (
                <p className="typography-caption1 font-light text-secondary">
                  Tracking: Available once shipped
                </p>
              )}
            </div>
          </SheetHeader>

          <div
            className="relative overflow-y-auto px-1 pb-8"
            data-testid="orderTracking-timelineContainer"
          >
            <OrderTrackingTimeline
              stepGroups={stepGroups}
              data-testid="orderTracking-timeline"
            />

            <p className="typography-caption1 font-light text-tertiary mt-8">
              The information here is the same information that our customer
              support team has access to.
            </p>
          </div>
        </SheetContent>
      </SheetPortal>
    </Sheet>
  );
};
